/**
 * Cliente HTTP dos front-ends.
 *
 * Concentra tres coisas que nao podem ficar espalhadas: o token da sessao, a
 * traducao de `problem+json` para uma mensagem legivel, e o redirecionamento
 * ao login quando a sessao expira. Componente de tela nunca conversa com
 * `fetch` diretamente.
 *
 * Virou fabrica porque o backoffice tinha uma copia byte a byte da versao do
 * portal, menos o envio de arquivo e o download autenticado — e era
 * exatamente o que faltava para a operacao conseguir subir um certificado em
 * nome do cliente. Duas copias de um cliente HTTP divergem na primeira
 * correcao que so uma delas recebe.
 */

export interface ProblemBody {
  title?: string;
  detail?: string;
  status?: number;
  errors?: Record<string, string[]>;
}

/** Erro com a mensagem que o usuario deve ler, ja separada dos detalhes. */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly problem?: ProblemBody,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** Erros por campo, no formato que os formularios consomem. */
  get fieldErrors(): Record<string, string> {
    const out: Record<string, string> = {};
    for (const [field, messages] of Object.entries(this.problem?.errors ?? {})) {
      out[field] = messages.join(' ');
    }
    return out;
  }
}

export interface ClienteHttp {
  getToken(): string | null;
  setToken(token: string | null): void;
  api<T>(path: string, init?: { method?: string; body?: unknown; idempotencyKey?: string }): Promise<T>;
  apiForm<T>(path: string, form: FormData): Promise<T>;
  apiDownload(path: string): Promise<void>;
  openDocumentFile(documentId: string, kind: 'xml' | 'pdf'): Promise<void>;
}

export interface OpcoesCliente {
  baseUrl: string;
  /** Chave no localStorage. Portal e backoffice usam chaves diferentes para
   *  que entrar num nao derrube a sessao do outro. */
  tokenKey: string;
  /** Chamado em 401. O padrao recarrega a pagina, o que leva ao login. */
  aoExpirar?: () => void;
}

export function criarClienteHttp(opcoes: OpcoesCliente): ClienteHttp {
  const { baseUrl: BASE, tokenKey: TOKEN_KEY } = opcoes;
  const aoExpirar = opcoes.aoExpirar ?? ((): void => window.location.reload());

  const getToken = (): string | null => {
    try {
      return localStorage.getItem(TOKEN_KEY);
    } catch {
      return null;
    }
  };

  const setToken = (token: string | null): void => {
    try {
      if (token) localStorage.setItem(TOKEN_KEY, token);
      else localStorage.removeItem(TOKEN_KEY);
    } catch {
      // Navegador com armazenamento bloqueado: a sessao dura o que durar a aba.
    }
  };

  const falhar = (status: number, payload: unknown): never => {
    const problem = payload as ProblemBody;
    if (status === 401) {
      setToken(null);
      aoExpirar();
    }
    throw new ApiError(problem.detail ?? problem.title ?? `Erro ${status}`, status, problem);
  };

  async function api<T>(
    path: string,
    init: { method?: string; body?: unknown; idempotencyKey?: string } = {},
  ): Promise<T> {
    const token = getToken();

    const res = await fetch(`${BASE}${path}`, {
      method: init.method ?? 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(init.idempotencyKey ? { 'Idempotency-Key': init.idempotencyKey } : {}),
      },
      ...(init.body !== undefined ? { body: JSON.stringify(init.body) } : {}),
    });

    if (res.status === 204) return undefined as T;

    const text = await res.text();
    const payload = text ? (JSON.parse(text) as unknown) : {};
    if (!res.ok) falhar(res.status, payload);
    return payload as T;
  }

  /**
   * Envia arquivo para a API (multipart).
   *
   * Nao reaproveita `api()` porque o `Content-Type` do multipart carrega o
   * `boundary`, que so o navegador sabe gerar: fixa-lo a mao quebra o upload
   * do certificado com um erro que nao diz o que houve.
   */
  async function apiForm<T>(path: string, form: FormData): Promise<T> {
    const token = getToken();

    const res = await fetch(`${BASE}${path}`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: form,
    });

    const text = await res.text();
    const payload = text ? (JSON.parse(text) as unknown) : {};
    if (!res.ok) falhar(res.status, payload);
    return payload as T;
  }

  /**
   * Baixa um arquivo gerado pela API.
   *
   * O navegador nao repassa o cabecalho `Authorization` num link comum, entao
   * o arquivo e buscado por `fetch` e entregue como blob. E o preco de manter
   * o endpoint autenticado — e um relatorio fiscal nao pode ficar acessivel
   * por URL adivinhavel.
   */
  async function apiDownload(path: string): Promise<void> {
    const token = getToken();

    const res = await fetch(`${BASE}${path}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });

    if (!res.ok) {
      const texto = await res.text();
      let problema: ProblemBody = {};
      try {
        problema = JSON.parse(texto) as ProblemBody;
      } catch {
        problema = {};
      }
      falhar(res.status, problema);
    }

    const nome =
      /filename="([^"]+)"/.exec(res.headers.get('content-disposition') ?? '')?.[1] ?? 'download';
    const url = URL.createObjectURL(await res.blob());

    const link = document.createElement('a');
    link.href = url;
    link.download = nome;
    link.click();

    // Sem o revoke o blob fica na memoria da aba ate a pagina recarregar.
    URL.revokeObjectURL(url);
  }

  /**
   * Abre o XML ou o documento auxiliar.
   *
   * A API devolve uma URL assinada de 15 minutos em vez do arquivo: o
   * download sai direto do armazenamento, sem passar pela aplicacao. Pedimos
   * JSON porque o navegador nao repassa o Authorization ao seguir um 302.
   */
  async function openDocumentFile(documentId: string, kind: 'xml' | 'pdf'): Promise<void> {
    const { url } = await api<{ url: string }>(`/v1/documents/${documentId}/${kind}`);
    window.open(url, '_blank', 'noopener');
  }

  return { getToken, setToken, api, apiForm, apiDownload, openDocumentFile };
}
